import type { Placement } from '@popperjs/core/lib/enums'
import cx from 'classnames'
import type React from 'react'
import { type ReactNode, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { type StrictModifier, usePopper } from 'react-popper'

import css from './Tooltip.module.scss'

type Props = {
  content: ReactNode
  renderEl: (
    onSetRef: (
      value:
        | ((prevState: HTMLElement | null) => HTMLElement | null)
        | HTMLElement
        | null
    ) => void,
    onMouseEnter: () => void,
    onMouseLeave: () => void,
    isOpen: boolean
  ) => ReactNode
  position?: Placement
  customClassName?: string
  offset?: {
    x?: number
    y?: number
  }
}

const tooltipPortal = document.querySelector('#tooltipPortal') as HTMLElement

export const Tooltip: React.FC<Props> = ({
  content,
  renderEl,
  position,
  customClassName,
  offset,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [modifiers, setModifiers] = useState<StrictModifier[]>([])
  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null)
  const [targetRef, setTargetRef] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (offset) {
      const x = offset.x || 0
      const y = offset.y || 0

      setModifiers((prevState) => {
        return [
          ...prevState,
          {
            name: 'offset',
            options: {
              offset: [x, y],
            },
          },
        ]
      })
    }
  }, [offset])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    }

    const removeOverflow = setTimeout(() => {
      document.body.style.overflow = 'auto'
    }, 1000)

    return () => clearInterval(removeOverflow)
  }, [isOpen])

  const { styles, attributes } = usePopper(targetRef, containerRef, {
    modifiers,
    placement: position || 'top',
  })

  const handleOnMouseEnter = () => {
    setIsOpen(true)
  }

  const handleOnMouseLeave = () => {
    setIsOpen(false)
  }

  return (
    <>
      {renderEl(setTargetRef, handleOnMouseEnter, handleOnMouseLeave, isOpen)}

      {isOpen &&
        createPortal(
          <div
            ref={setContainerRef}
            style={styles.popper}
            {...attributes.popper}
            className={cx(css.container, customClassName)}
          >
            {content}
          </div>,
          tooltipPortal
        )}
    </>
  )
}
